// "Act as" + "Places" for a support visit (CR_20260924 Part C): the role (Manager · Warehouse staff · Project user)
// and, for the two staff roles, their reach — all, or chosen places of the kind that role is given (decision E).
// Used by the start dialog and the banner's Change role dialog.
import {
  Alert,
  Checkbox,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  Radio,
  RadioGroup,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import {
  placeKindsFor,
  SUPPORT_ROLES,
  type SupportAct,
  togglePlace,
  withRole,
} from '@fleetspoke/core/support';
import { useTranslation } from 'react-i18next';
import { useSupportRolePlaces } from '../data/support';
import { LoadError, Loading } from './Page';

export function SupportActFields({
  companyId,
  value,
  onChange,
  disabled,
}: {
  companyId: string;
  value: SupportAct;
  onChange(next: SupportAct): void;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const choosing = value.role !== 'manager' && value.scope === 'selected';
  const places = useSupportRolePlaces(companyId, choosing);
  const kinds = placeKindsFor(value.role);
  const options = (places.data ?? []).filter((p) => kinds.includes(p.kind));
  const picked = (id: string) => value.siteIds.includes(id) || value.projectIds.includes(id);

  return (
    <Stack spacing={1}>
      <Typography variant="subtitle2">{t('support.act_as')}</Typography>
      <ToggleButtonGroup
        exclusive
        size="small"
        color="primary"
        value={value.role}
        onChange={(_, role) => role && onChange(withRole(role))}
        disabled={disabled}
        sx={{ flexWrap: 'wrap' }}
      >
        {SUPPORT_ROLES.map((role) => (
          <ToggleButton key={role} value={role} data-testid={`support-act-${role}`}>
            {t(`settings.users.role_${role}`)}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
      <FormHelperText sx={{ mt: 0 }}>{t('support.act_help')}</FormHelperText>

      {value.role !== 'manager' ? (
        <>
          <Typography variant="subtitle2" sx={{ pt: 1 }}>
            {t('support.reach')}
          </Typography>
          <RadioGroup
            row
            value={value.scope}
            onChange={(e) =>
              onChange({ ...withRole(value.role), scope: e.target.value as SupportAct['scope'] })
            }
          >
            <FormControlLabel
              value="all"
              disabled={disabled}
              control={<Radio size="small" />}
              label={t(
                value.role === 'warehouse_staff'
                  ? 'support.reach_all_warehouses'
                  : 'support.reach_all_projects',
              )}
              data-testid="support-reach-all"
            />
            <FormControlLabel
              value="selected"
              disabled={disabled}
              control={<Radio size="small" />}
              label={t('support.reach_choose')}
              data-testid="support-reach-choose"
            />
          </RadioGroup>
          {choosing ? (
            places.isLoading ? (
              <Loading />
            ) : places.error ? (
              <LoadError error={places.error} />
            ) : options.length === 0 ? (
              <Alert severity="info">{t('support.no_places')}</Alert>
            ) : (
              <FormGroup sx={{ maxHeight: 220, overflowY: 'auto', pl: 1 }}>
                {options.map((p) => (
                  <FormControlLabel
                    key={p.id}
                    disabled={disabled}
                    control={
                      <Checkbox
                        size="small"
                        checked={picked(p.id)}
                        onChange={() => onChange(togglePlace(value, p))}
                      />
                    }
                    label={
                      value.role === 'project_user'
                        ? `${p.name} · ${t(p.kind === 'project' ? 'm.place.kind_project' : 'requests.location')}`
                        : p.name
                    }
                    data-testid={`support-place-${p.name}`}
                  />
                ))}
              </FormGroup>
            )
          ) : null}
          {choosing && value.siteIds.length + value.projectIds.length === 0 ? (
            <FormHelperText error sx={{ mt: 0 }}>
              {t('support.reach_none_picked')}
            </FormHelperText>
          ) : null}
        </>
      ) : null}
    </Stack>
  );
}
